"use client";

import * as Dialog from "@radix-ui/react-dialog";
import { useTranslations } from "next-intl";
import { useAccessRestrictedStore } from "@/stores/access-restricted-store";

function LockIcon({ size = 28, className }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className}>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 018 0v4" />
    </svg>
  );
}

function XIcon({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" className={className}>
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/** App-wide modal for any 403 the API returns — see throwIfNotOk in lib/api/http-error.ts. */
export function AccessRestrictedModal() {
  const t = useTranslations("accessRestricted");
  const isOpen = useAccessRestrictedStore((s) => s.isOpen);
  const message = useAccessRestrictedStore((s) => s.message);
  const hide = useAccessRestrictedStore((s) => s.hide);

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && hide()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-100 bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-100 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-8 text-center shadow-2xl focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        >
          <Dialog.Close
            aria-label={t("close")}
            className="absolute right-4 top-4 text-muted transition-colors hover:text-secondary"
          >
            <XIcon size={16} />
          </Dialog.Close>

          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
            <LockIcon size={28} className="text-red-500" />
          </div>

          <Dialog.Title className="mt-4 text-lg font-semibold text-secondary">
            {t("title")}
          </Dialog.Title>
          <Dialog.Description className="mt-1.5 text-sm text-muted">
            {message}
          </Dialog.Description>

          <button
            type="button"
            onClick={hide}
            className="mt-5 w-full rounded-xl bg-secondary-950 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-secondary-900"
          >
            {t("ok")}
          </button>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
